import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { APPLICATION_SCORECARD_API } from './api';
import {
  extractScorecards,
  isTransferredCandidate,
  transformScorecardsToTableFormat,
} from './helper';

/**
 * The submitted ("completed") scorecards for an application — transferred ones
 * included.
 *
 * `extractScorecards` derives scorecards by walking THIS application's
 * `ats_job_stages`, so it structurally cannot see a scorecard that belongs to
 * the candidate's earlier application. A transferred candidate whose current
 * application has none of its own therefore reads as "no scorecards at all",
 * which is what made the offer-approval note refuse to insert them (ENG-3847).
 *
 * The API view returns both, so a transferred candidate is served from it —
 * the same split `ApplicationScorecard` makes. A non-transferred candidate
 * keeps the derived path and fires no request.
 */
export const useCompletedScorecards = (applicationData: any) => {
  const applicationId = applicationData?.id;
  const transferred = isTransferredCandidate(applicationData);

  const { data, isLoading, isError } = useQuery({
    // Shares the tab's cache key so opening the offer drawer after the
    // scorecard tab reuses the response instead of refetching it.
    queryKey: ['application-scorecards-completed', applicationId, 'submitted'],
    queryFn: () =>
      APPLICATION_SCORECARD_API.getApplicationScorecards(applicationId, [
        'submitted',
      ]),
    enabled: Boolean(applicationId) && transferred,
  });

  return useMemo(() => {
    if (!transferred) {
      return {
        completed: extractScorecards(applicationData || {}).completed,
        isLoading: false,
        isError: false,
      };
    }

    // The endpoint has been seen wrapping its payload, so accept both shapes.
    const scorecards = data?.scorecards ?? data?.data?.scorecards;

    return {
      completed: transformScorecardsToTableFormat(scorecards, applicationData),
      isLoading,
      isError,
    };
  }, [transferred, applicationData, data, isLoading, isError]);
};
